import React from "react";
import { Card, Tabs, Tab, SxProps, Theme } from "@mui/material";
import { SvgIconComponent } from "@mui/icons-material";
import { transparentColors, boxShadows } from "../utils/theme";

/**
 * Tab configuration interface
 */
export interface TabConfig {
  /** Unique value for the tab */
  value: string | number;
  /** Display label for the tab */
  label: string;
  /** Optional icon component */
  icon?: React.ReactElement<SvgIconComponent>;
}

/**
 * ViewTabs Component Props
 */
interface ViewTabsProps {
  /** Current active tab value */
  value: string | number;
  /** Tab change handler */
  onChange: (event: React.SyntheticEvent, newValue: any) => void;
  /** Array of tab configurations */
  tabs: TabConfig[];
  /** Whether to display in full width mode (default: true) */
  fullWidth?: boolean;
  /** Custom sx props for the Card wrapper */
  cardSx?: SxProps<Theme>;
  /** Custom sx props for the Tabs component */
  tabsSx?: SxProps<Theme>;
}

/**
 * ViewTabs Component
 *
 * Reusable glassmorphic tab navigation component used in Dashboard and Analytics screens.
 * Features:
 * - Glassmorphism design with backdrop blur
 * - Smooth hover animations
 * - Icon support with flexible positioning
 * - Responsive and accessible
 * - Consistent styling with theme constants
 *
 * @example
 * ```tsx
 * <ViewTabs
 *   value={activeTab}
 *   onChange={(_, newValue) => setActiveTab(newValue)}
 *   tabs={[
 *     { value: 0, label: "Daily Analytics", icon: <CalendarToday /> },
 *     { value: 1, label: "Monthly Analytics", icon: <ShowChart /> },
 *     { value: 2, label: "Yearly Analytics", icon: <Assessment /> }
 *   ]}
 * />
 * ```
 */
const ViewTabs: React.FC<ViewTabsProps> = ({
  value,
  onChange,
  tabs,
  fullWidth = true,
  cardSx,
  tabsSx,
}) => {
  return (
    <Card
      sx={{
        background: transparentColors.white15,
        backdropFilter: "blur(20px)",
        border: `1px solid ${transparentColors.white25}`,
        borderRadius: 3,
        mt: 3,
        boxShadow: boxShadows.card,
        ...cardSx,
      }}
    >
      <Tabs
        value={value}
        onChange={onChange}
        variant={fullWidth ? "fullWidth" : "standard"}
        sx={{
          "& .MuiTab-root": {
            color: transparentColors.white70,
            py: 2.5,
            textTransform: "none",
            fontSize: "1rem",
            fontWeight: 600,
            letterSpacing: "0.3px",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              color: "white",
              background: transparentColors.white10,
              transform: "translateY(-1px)",
            },
          },
          "& .Mui-selected": {
            color: "white !important",
          },
          "& .MuiTabs-indicator": {
            height: 4,
            backgroundColor: "white",
            borderRadius: "4px 4px 0 0",
            boxShadow: "0 -2px 8px rgba(255,255,255,0.3)",
          },
          ...tabsSx,
        }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.value}
            label={tab.label}
            value={tab.value}
            icon={tab.icon}
            iconPosition="start"
          />
        ))}
      </Tabs>
    </Card>
  );
};

export default ViewTabs;
